import type { DateTime } from 'luxon'

import type {
  BidEnergyActivationGroup,
  BidEnergyPtu,
  BidEnergyStep,
} from '@/features/bidding/types/bidDetail/BidEnergyPtu'
import type {
  EnergyBidPtuSnapshot,
  EnergyBidSnapshot,
  EnergyBidStepSnapshot,
} from '@/features/bidding/types/bidDetail/EnergyBidSnapshot'
import type { CapacityObligation } from '@/features/bidding/types/capacityObligations'
import { computeObligationByActivationGroupPtu } from '@/features/bidding/utils/bidDetail/computeObligationByActivationGroupPtu'
import type { PtuRollup } from '@/features/bidding/utils/bidDetail/computePtuObligationVsOffered'
import { computePtuObligationVsOffered } from '@/features/bidding/utils/bidDetail/computePtuObligationVsOffered'
import { getRowStateClasses } from '@/features/bidding/utils/bidDetail/getRowStateClasses'
import { sumPtuOfferedWatts } from '@/features/bidding/utils/bidDetail/sumPtuOfferedWatts'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'

export type BuildBidEnergyPtusInputs = Readonly<{
  snapshots: ReadonlyArray<EnergyBidSnapshot>
  obligations: ReadonlyArray<CapacityObligation>
  /** UUIDs the user picked in the activation group multi-select. Empty = nothing rendered. */
  selectedActivationGroupUuids: ReadonlyArray<string>
  /** Master activation group list used to resolve the display `code`; falls back to the snapshot's `activationGroupCode`. */
  activationGroupOptions?: ReadonlyArray<{ uuid: string; code: string }>
  /** Inclusive index range into the day's PTU list. */
  ptuRange: Readonly<{ fromIndex: number; toIndex: number }>
  ptuLengthInMinutes: number
  deliveryDay: MarketDate
  now: DateTime
  /** When `true` (default), zero-volume steps are dropped, along with any group or PTU thereby left empty. */
  hideZeroVolumeRows?: boolean
}>

const EMPTY_ROLLUP: PtuRollup = { offeredWatts: 0, obligationWatts: 0 }

type ResolveCode = (snapshot: EnergyBidSnapshot) => string
type DayPtu = ReturnType<MarketDate['ptus']>[number]

/**
 * Pure transform from query results + filter state into the PTU → activation
 * group → step tree the energy table renders. Reads as a pipeline: scope the
 * data to the selection, bucket each group's offers by PTU, then assemble the
 * PTU nodes across the visible range.
 *
 * OBL / OFF stats sum across the selected activation groups only — both streams
 * are scoped to the same selection so the rollup stays comparable. Ordering:
 * PTU ascending → activation group code alphabetical → step.
 */
export function buildBidEnergyPtus(inputs: BuildBidEnergyPtusInputs): BidEnergyPtu[] {
  const {
    snapshots,
    obligations,
    selectedActivationGroupUuids,
    activationGroupOptions = [],
    ptuRange,
    ptuLengthInMinutes,
    deliveryDay,
    now,
    hideZeroVolumeRows = true,
  } = inputs

  const selectedUuids = new Set(selectedActivationGroupUuids)
  if (selectedUuids.size === 0 || snapshots.length === 0) {
    return []
  }

  const resolveCode = makeCodeResolver(activationGroupOptions)
  const selectedSnapshots = selectSortedSnapshots(snapshots, selectedUuids, resolveCode)
  const selectedObligations = obligations.filter((o) => selectedUuids.has(o.activationGroupUuid))

  const rollupByPtuStart = computePtuObligationVsOffered(selectedSnapshots, selectedObligations, ptuLengthInMinutes)
  const obligationByActivationGroupPtu = computeObligationByActivationGroupPtu(selectedObligations, ptuLengthInMinutes)

  const groupsByPtuStart = groupActivationGroupsByPtuStart(selectedSnapshots, {
    resolveCode,
    obligationByActivationGroupPtu,
    hideZeroVolumeRows,
  })

  const visiblePtus = filterPtus(deliveryDay, ptuLengthInMinutes, ptuRange)
  return buildPtus(visiblePtus, groupsByPtuStart, rollupByPtuStart, now)
}

function makeCodeResolver(options: ReadonlyArray<{ uuid: string; code: string }>): ResolveCode {
  const codeByUuid = new Map(options.map((option) => [option.uuid, option.code]))
  return (snapshot) => codeByUuid.get(snapshot.context.activationGroupUuid) ?? snapshot.context.activationGroupCode
}

function selectSortedSnapshots(
  snapshots: ReadonlyArray<EnergyBidSnapshot>,
  selectedUuids: ReadonlySet<string>,
  resolveCode: ResolveCode,
): EnergyBidSnapshot[] {
  return snapshots
    .filter((snapshot) => selectedUuids.has(snapshot.context.activationGroupUuid))
    .sort((a, b) => resolveCode(a).localeCompare(resolveCode(b)))
}

type GroupContext = Readonly<{
  resolveCode: ResolveCode
  obligationByActivationGroupPtu: ReadonlyMap<string, number>
  hideZeroVolumeRows: boolean
}>

function groupActivationGroupsByPtuStart(
  selectedSnapshots: ReadonlyArray<EnergyBidSnapshot>,
  context: GroupContext,
): Map<number, BidEnergyActivationGroup[]> {
  const groupsByPtuStart = new Map<number, BidEnergyActivationGroup[]>()
  for (const snapshot of selectedSnapshots) {
    for (const ptuSnapshot of snapshot.ptus) {
      const group = buildActivationGroup(snapshot, ptuSnapshot, context)
      if (group === null) {
        continue
      }
      const ptuStart = ptuSnapshot.ptu.start.toMillis()
      const groupsAtPtu = groupsByPtuStart.get(ptuStart)
      if (groupsAtPtu) {
        groupsAtPtu.push(group)
      } else {
        groupsByPtuStart.set(ptuStart, [group])
      }
    }
  }
  return groupsByPtuStart
}

function buildActivationGroup(
  snapshot: EnergyBidSnapshot,
  ptuSnapshot: EnergyBidPtuSnapshot,
  context: GroupContext,
): BidEnergyActivationGroup | null {
  const steps = toLadderSteps(ptuSnapshot.steps, context.hideZeroVolumeRows)
  if (steps.length === 0) {
    return null
  }
  const uuid = snapshot.context.activationGroupUuid
  const ptuStart = ptuSnapshot.ptu.start.toMillis()
  const offeredWatts = sumPtuOfferedWatts(ptuSnapshot)
  return {
    uuid,
    code: context.resolveCode(snapshot),
    source: ptuSnapshot.source,
    obligationWatts: context.obligationByActivationGroupPtu.get(`${uuid}-${ptuStart}`) ?? 0,
    offeredWatts,
    dimmed: offeredWatts === 0,
    steps,
  }
}

function toLadderSteps(steps: ReadonlyArray<EnergyBidStepSnapshot>, hideZeroVolumeRows: boolean): BidEnergyStep[] {
  return steps
    .map((step, index) => ({ step, index, isZero: step.offeredVolume.quantity === 0 }))
    .filter((ladderStep) => !hideZeroVolumeRows || !ladderStep.isZero)
}

function filterPtus(
  deliveryDay: MarketDate,
  ptuLengthInMinutes: number,
  ptuRange: Readonly<{ fromIndex: number; toIndex: number }>,
): DayPtu[] {
  const dayPtus = deliveryDay.ptus({ minutes: ptuLengthInMinutes })
  const fromIndex = Math.max(0, Math.min(ptuRange.fromIndex, dayPtus.length - 1))
  const toIndex = Math.max(fromIndex, Math.min(ptuRange.toIndex, dayPtus.length - 1))
  return dayPtus.slice(fromIndex, toIndex + 1)
}

function buildPtus(
  visiblePtus: ReadonlyArray<DayPtu>,
  groupsByPtuStart: ReadonlyMap<number, BidEnergyActivationGroup[]>,
  rollupByPtuStart: ReadonlyMap<number, PtuRollup>,
  now: DateTime,
): BidEnergyPtu[] {
  return visiblePtus.flatMap((dayPtu) => {
    const activationGroups = groupsByPtuStart.get(dayPtu.start.toMillis())
    if (!activationGroups) {
      return []
    }
    const rollup = rollupByPtuStart.get(dayPtu.start.toMillis()) ?? EMPTY_ROLLUP
    return [buildPtu(dayPtu, activationGroups, rollup, now)]
  })
}

/** Assemble a PTU node from its visible groups and the selection-scoped rollup. */
function buildPtu(
  dayPtu: DayPtu,
  activationGroups: BidEnergyActivationGroup[],
  rollup: PtuRollup,
  now: DateTime,
): BidEnergyPtu {
  return {
    start: dayPtu.start,
    end: dayPtu.end,
    source: activationGroups[0].source,
    offeredWatts: rollup.offeredWatts,
    // Dim the (rowspan'd) PTU cell only when every group in it offered nothing.
    dimmed: rollup.offeredWatts === 0,
    state: getRowStateClasses({
      ptuStart: dayPtu.start,
      ptuEnd: dayPtu.end,
      now,
      activated: false,
      underOffered: rollup.obligationWatts > 0 && rollup.offeredWatts < rollup.obligationWatts,
    }),
    activationGroups,
  }
}
